import React from 'react';
import { kebabCase } from 'lodash';
import { useField, useFormikContext } from 'formik';
import { Select } from 'syngenta-digital-cropwise-react-ui-kit';
import SearchIcon from 'assets/search.svg?react';
import { FieldMessage } from './FieldChrome';
import useFieldMessage from './useFieldMessage';
import { FieldItem, FieldKey } from './types';
import './FormikSearchSelect.css';

type FormikSearchSelectProps = {
  name: string;
  items: FieldItem[] | null | undefined;
  id?: string;
  // The field's accessible name. Not drawn: grow-ui's Search never showed
  // its label, and callers render their own.
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  errorMessage?: string | null;
  // Replaces the default "store the key" behaviour, as it did on grow-ui.
  onSelect?: (item: FieldItem) => void;
  // Shows this key instead of the field's own value.
  selectedValue?: FieldKey;
  'data-testid'?: string;
};

const matchesSearch = (input: string, label: unknown) =>
  typeof label === 'string' &&
  label.toUpperCase().includes(input.toUpperCase());

const FormikSearchSelect = ({
  name,
  items,
  id,
  label,
  placeholder,
  disabled = false,
  errorMessage,
  onSelect,
  selectedValue,
  'data-testid': dataTestId
}: FormikSearchSelectProps) => {
  const [field] = useField(name);
  const { setFieldValue, setFieldTouched } = useFormikContext();
  const { hasError, message } = useFieldMessage(name, errorMessage);
  const selectId = id ?? kebabCase(name);

  const list = items ?? [];
  // Keyed by position: antd only accepts string or number option values.
  const options = list.map((item, index) => ({
    value: index,
    label: item.value
  }));
  const current = selectedValue !== undefined ? selectedValue : field.value;
  const selectedIndex = list.findIndex(item => item.key === current);

  const handleChange = (index: number | undefined) => {
    // Clearing always empties the field, even when the caller handles
    // selection itself; grow-ui's Search did the same on an emptied input.
    if (index === undefined) {
      setFieldValue(name, null);
      return;
    }
    const item = list[index];
    if (onSelect) {
      onSelect(item);
      return;
    }
    setFieldTouched(name, true);
    setFieldValue(name, item.key);
  };

  return (
    <div
      className={`relative cwf-kit-search-select ${
        disabled ? 'cwf-kit-search-select--disabled' : ''
      }`}
      data-testid={dataTestId}
    >
      <span className="cwf-kit-search-select__icon" aria-hidden="true">
        <SearchIcon width={16} height={16} />
      </span>
      <Select
        id={selectId}
        aria-label={label}
        className="w-full"
        showSearch
        allowClear
        value={selectedIndex === -1 ? undefined : selectedIndex}
        placeholder={placeholder}
        disabled={disabled}
        options={options}
        filterOption={(input, option) => matchesSearch(input, option?.label)}
        onChange={handleChange}
        status={hasError ? 'error' : undefined}
        suffixIcon={null}
      />
      <FieldMessage message={message} />
    </div>
  );
};

export default FormikSearchSelect;
